"use client";

import { Archive, ClipboardPlus, History, Link, Loader2, Mic, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { DeveloperButton } from "@/components/shared/DeveloperButton";
import { SyncStatus } from "@/components/shared/SyncStatus";
import { Button } from "@/components/ui/button";
import { trackVoiceInputTapped } from "@/lib/analytics";
import type { SyncState } from "@/lib/types";
import { cn } from "@/lib/utils";

import { ImportUrlModal } from "./ImportUrlModal";

interface IngredientActionBarProps {
  syncState: SyncState;
  isSyncing: boolean;
  onManualSync: () => Promise<void> | void;
  onOpenArchive: () => void;
  onArchiveAll: () => void;
  onDeleteAll: () => void;
  // Developer-only
  isDeveloperMode?: boolean;
  onAddBulkIngredients?: () => void;
  hasActiveIngredients: boolean;
  // Voice input
  isListening?: boolean;
  isProcessing?: boolean;
  isSupported?: boolean;
  isPermissionGranted?: boolean | null;
  onVoiceToggle?: () => void;
  // URL import
  onImportIngredients?: (ingredients: string[]) => void;
  onRequireAuth?: () => boolean;
}

export function IngredientActionBar({
  syncState,
  isSyncing,
  onManualSync,
  onOpenArchive,
  onArchiveAll,
  onDeleteAll,
  isDeveloperMode = false,
  onAddBulkIngredients,
  hasActiveIngredients,
  isListening = false,
  isProcessing = false,
  isSupported = false,
  isPermissionGranted = null,
  onVoiceToggle,
  onImportIngredients,
  onRequireAuth,
}: IngredientActionBarProps) {
  const t = useTranslations("ingredients");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showImportUrlModal, setShowImportUrlModal] = useState(false);

  // Show voice button if supported AND (permission granted/prompt OR still checking)
  const showVoiceButton = isSupported && isPermissionGranted !== false && onVoiceToggle;

  const handleImportClick = () => {
    if (onRequireAuth && !onRequireAuth()) return;
    setShowImportUrlModal(true);
  };

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        {/* Left group - existing action buttons */}
        <div className="flex flex-wrap items-center gap-4">
          <SyncStatus syncState={syncState} isSyncing={isSyncing} onManualSync={onManualSync} />
          <Button
            variant="mute"
            size="auto"
            onClick={onOpenArchive}
            title={t("viewArchived")}
            aria-label={t("viewArchived")}
          >
            <History className="size-6" strokeWidth={1.5} />
          </Button>

          <Button
            variant="mute"
            size="auto"
            onClick={() => setShowArchiveDialog(true)}
            title={t("archiveAll")}
            aria-label={t("archiveAll")}
            disabled={!hasActiveIngredients}
          >
            <Archive className="size-6" strokeWidth={1.5} />
          </Button>
          <Button
            variant="mute"
            size="auto"
            onClick={() => setShowDeleteDialog(true)}
            title={t("deleteAll")}
            aria-label={t("deleteAll")}
            disabled={!hasActiveIngredients}
          >
            <Trash2 className="size-6" strokeWidth={1.5} />
          </Button>
          {/* Developer-only: Bulk ingredients button */}
          {isDeveloperMode && onAddBulkIngredients && (
            <DeveloperButton
              variant="mute"
              onClick={onAddBulkIngredients}
              title={t("addSample")}
              aria-label={t("addSample")}
            >
              <ClipboardPlus className="size-6" strokeWidth={1.5} />
            </DeveloperButton>
          )}
        </div>

        {/* Right group - import URL and voice input buttons */}
        <div className="flex items-center gap-4">
          {/* Import from URL button */}
          {onImportIngredients && (
            <Button
              variant="mute"
              size="auto"
              onClick={handleImportClick}
              title={t("importFromUrl")}
              aria-label={t("importFromUrl")}
            >
              <Link className="size-6" strokeWidth={1.5} />
            </Button>
          )}

          {/* Voice input button */}
          {showVoiceButton && (
            <Button
              variant="mute"
              size="auto"
              onClick={() => {
                // Only track when permission is explicitly granted and the user is starting to listen
                if (isPermissionGranted === true && !isListening) {
                  trackVoiceInputTapped();
                }

                onVoiceToggle?.();
              }}
              disabled={isProcessing}
              className={cn(
                "transition-all",
                isListening &&
                  "animate-pulse bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                isProcessing && "cursor-not-allowed opacity-80"
              )}
              title={isListening ? t("listening") : t("voiceInput")}
              aria-label={isListening ? t("listening") : t("voiceInput")}
            >
              {isProcessing ? (
                <Loader2 className="size-6 animate-spin" strokeWidth={1.5} />
              ) : (
                <Mic
                  className={cn("size-6 transition-transform", isListening && "scale-110")}
                  strokeWidth={1.5}
                />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Import URL Modal */}
      {onImportIngredients && (
        <ImportUrlModal
          isOpen={showImportUrlModal}
          onClose={() => setShowImportUrlModal(false)}
          onImport={onImportIngredients}
        />
      )}

      <ConfirmDialog
        isOpen={showArchiveDialog}
        onClose={() => setShowArchiveDialog(false)}
        onConfirm={onArchiveAll}
        title={t("archiveAllTitle")}
        description={t("archiveAllDescription")}
        confirmText={t("archive")}
        confirmIcon={<Archive className="size-4" />}
        cancelText={t("cancel")}
        variant="default"
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={onDeleteAll}
        title={t("deleteAllTitle")}
        description={t("deleteAllDescription")}
        confirmText={t("delete")}
        confirmIcon={<Trash2 className="size-4" />}
        cancelText={t("cancel")}
        variant="destructive"
      />
    </>
  );
}
